import React, {
    ReactElement,
    useState,
    useMemo,
    forwardRef,
    ReactNode,
    CSSProperties,
    useRef,
    useCallback,
    useLayoutEffect,
    useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { debounce } from 'lodash';
import styled, { css } from 'styled-components';
import { DatePicker as MotiliUIDatePicker } from '@motili/ui';
import moment from 'moment-timezone';
import { RenderMonthElementProps } from 'common/types/date-time';
import { Z_INDEX } from 'common/utils/constants';
import DatePickerLabel from './DatePickerLabel';
import { getDateFormat } from '../../utils/date-time';
import DatePickerMonthList from './DatePickerMonthList';

const DatePicker = (props: any): ReactElement => {
    const { ...otherProps } = props;

    const updatedProps = {
        ...defaultProps,
        ...otherProps,
    };

    const portalRef = useRef<HTMLDivElement>(null);
    const datePickerRef = useRef<HTMLDivElement>(null);
    const [focused, setFocused] = useState(updatedProps.focused || false);
    const [currentMonth, setCurrentMonth] = useState(moment().month());
    const [currentYear, setCurrentYear] = useState(moment().year());
    const [portalCoordinates, setPortalCoordinates] = useState({
        top: 0,
        left: 0,
    });

    const updatePortalCoordinates = useCallback(() => {
        if (datePickerRef.current) {
            const rect = datePickerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const isAboveHalf = rect.top < windowHeight / 2;
            const aboveHalfDisplacement = isAboveHalf ? 20 : 365;
            if (updatedProps.fixedPortal) {
                setPortalCoordinates({
                    top: rect.bottom - aboveHalfDisplacement,
                    left: rect.left,
                });
            } else {
                setPortalCoordinates({
                    top: rect.bottom + window.scrollY - aboveHalfDisplacement,
                    left: rect.left + window.scrollX,
                });
            }
        }
    }, [updatedProps.fixedPortal]);

    const earliestYear = useMemo(() => {
        return updatedProps.yearMin || moment().year() - 1;
    }, [updatedProps.yearMin]);
    const latestYear = useMemo(() => {
        return moment().year() + 3;
    }, []);

    const date = useMemo(() => {
        return typeof updatedProps.date === 'string'
            ? updatedProps.date.length && updatedProps.date !== 'Invalid date'
                ? moment(updatedProps.date)
                : null
            : updatedProps.date;
    }, [updatedProps.date]);

    const onDateChange = (date: any) => {
        if (updatedProps.showClearDate && !date && updatedProps.date) {
            return updatedProps.onDateChange(date);
        }
        if (!updatedProps.date || date) {
            return updatedProps.onDateChange(date);
        }
        return updatedProps.onDateChange(updatedProps.date);
    };

    const onFocusChange = ({ focused }: any) => {
        if (updatedProps.onFocusChange) {
            updatedProps.onFocusChange({ focused });
        }
    };

    const adjustMinDate = (day: any, minDate: any) =>
        day.isBefore(minDate, 'day');

    const isDayBlocked = (day: any) => {
        if (updatedProps.isDayBlocked) {
            return updatedProps.isDayBlocked(day);
        }
        return false;
    };

    const isDisableMonthLeft = (month: number, year: number) => {
        return month === 0 && year === earliestYear;
    };

    const isDisableMonthRight = (month: number, year: number) => {
        return month === 11 && year === latestYear;
    };

    const portalStyle: CSSProperties = {
        position: updatedProps.fixedPortal ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        transform: `translate3d(${portalCoordinates.left + 1}px, ${
            portalCoordinates.top - 45
        }px, 0)`,
        zIndex: updatedProps.zIndex || Z_INDEX.dropdownPortal,
    };

    const handleDocumentClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (
            datePickerRef.current &&
            !datePickerRef.current.contains(e.target as Node)
        ) {
            setFocused(false);
        }
    };

    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const target = e.target as HTMLElement;
        const openPicker = document.querySelector('.openPicker');
        if (openPicker && openPicker !== datePickerRef.current) {
            openPicker.classList.remove('openPicker');
            openPicker.dispatchEvent(new Event('loseFocus'));
        }

        if (focused && !target.classList.contains('DateInput_input')) {
            setFocused(false);
            onDateChange(date);
        } else if (
            !target.classList.contains('SingleDatePickerInput_clearDate') &&
            !target.classList.contains('SingleDatePickerInput_clearDate_svg') &&
            !target.parentElement?.classList.contains(
                'SingleDatePickerInput_clearDate_svg'
            )
        ) {
            setFocused(true);
        }
    };

    useEffect(() => {
        const handleLoseFocus = () => {
            setFocused(false);
        };
        const datePickerRefCurrent = datePickerRef.current;
        if (focused) {
            document.addEventListener('click', handleDocumentClick);
            datePickerRefCurrent?.addEventListener(
                'loseFocus',
                handleLoseFocus
            );
            if (updatedProps.usePortal) {
                updatePortalCoordinates();
            }
        } else {
            document.removeEventListener('click', handleDocumentClick);
        }

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [focused]);

    useLayoutEffect(() => {
        if (updatedProps.usePortal) {
            const debouncedUpdate = debounce(updatePortalCoordinates, 250);
            window.addEventListener('resize', debouncedUpdate);
            updatePortalCoordinates();
            return () => {
                window.removeEventListener('resize', debouncedUpdate);
                debouncedUpdate.cancel();
            };
        }
        return () => {};
    }, [updatedProps.usePortal, updatePortalCoordinates]);

    const datePickerContainerComponent = (
        <StyledDatePickerWrapped
            {...updatedProps}
            date={date}
            renderMonthElement={({
                month: date,
                onMonthSelect,
                onYearSelect,
            }: RenderMonthElementProps) => {
                return (
                    <DatePickerMonthList
                        month={date}
                        onMonthSelect={(date, month) => {
                            setCurrentMonth(Number(month));
                            onMonthSelect(date, month);
                        }}
                        onYearSelect={(date, year) => {
                            setCurrentYear(Number(year));
                            onYearSelect(date, year);
                        }}
                        yearMin={updatedProps.yearMin}
                    />
                );
            }}
            onChange={(date: any) => {
                onDateChange(date);
                if (focused) {
                    setFocused(false);
                }
            }}
            focused={focused}
            setFocused={() => {}}
            onFocusChange={onFocusChange}
            isDayBlocked={(day: moment.Moment) => {
                return updatedProps.minDate
                    ? adjustMinDate(day, updatedProps.minDate)
                    : isDayBlocked(day);
            }}
            borderColor={
                updatedProps.borderColor ? updatedProps.borderColor : '#ccc'
            }
            onPrevMonthClick={() => {
                if (currentMonth === 0) {
                    setCurrentMonth(11);
                    setCurrentYear(currentYear - 1);
                } else {
                    setCurrentMonth(currentMonth - 1);
                }
            }}
            onNextMonthClick={() => {
                if (currentMonth === 11) {
                    setCurrentMonth(0);
                    setCurrentYear(currentYear + 1);
                } else {
                    setCurrentMonth(currentMonth + 1);
                }
            }}
        />
    );

    return (
        <>
            {updatedProps.label && (
                <DatePickerLabel
                    required={updatedProps.required}
                    label={updatedProps.label}
                />
            )}
            <DatePickerContainer
                className={`${focused ? 'openPicker' : ''} datePickerContainer`}
                data-test-id={updatedProps.dataTestId}
                disableMonthLeft={isDisableMonthLeft(currentMonth, currentYear)}
                disableMonthRight={isDisableMonthRight(
                    currentMonth,
                    currentYear
                )}
                zIndex={updatedProps.zIndex}
                height={updatedProps.height}
                ref={datePickerRef}
                hasPortal={updatedProps.usePortal}
                onClick={handleContainerClick}
            >
                {datePickerContainerComponent}
                {updatedProps.usePortal && (
                    <PortalContent ref={portalRef} style={portalStyle}>
                        <PortalContainer>
                            {datePickerContainerComponent}
                        </PortalContainer>
                    </PortalContent>
                )}
            </DatePickerContainer>
        </>
    );
};

const defaultProps: any = {
    isDayBlocked: () => false,
    id: 'datePicker',
    disabled: false,
    displayFormat: getDateFormat(),
    numberOfMonths: 1,
    height: '40px',
};

const focusVisibleMixin = css`
    &:focus-visible {
        outline: none !important;
    }
`;
const DatePickerContainer = styled.div<{
    disableMonthLeft?: boolean;
    disableMonthRight?: boolean;
    zIndex?: number;
    height?: string;
    hasPortal?: boolean;
}>`
    .DayPickerNavigation_leftButton__horizontalDefault {
        pointer-events: ${props => (props.disableMonthLeft ? 'none' : 'auto')};
        background-color: ${props =>
            props.disableMonthLeft ? '#e3e3e3' : '#fff'};
    }
    .DayPickerNavigation_rightButton__horizontalDefault {
        pointer-events: ${props => (props.disableMonthRight ? 'none' : 'auto')};
        background-color: ${props =>
            props.disableMonthRight ? '#e3e3e3' : '#fff'};
    }
    .SingleDatePicker {
        height: ${props => props.height};
    }
    .SingleDatePicker_picker {
        z-index: ${props => props.zIndex};
    }
    .SingleDatePicker_picker {
        display: ${props => (props.hasPortal ? 'none' : 'block')};
    }
    .DateInput_fang {
        display: ${props => (props.hasPortal ? 'none' : 'block')};
    }
    .SingleDatePickerInput {
        ${focusVisibleMixin}
        .DateInput {
            ${focusVisibleMixin}

            .DateInput_input {
                ${focusVisibleMixin}
            }
        }
    }
`;

const PortalContainer = styled.div`
    .DateInput {
        display: none;
    }
    .SingleDatePickerInput_clearDate {
        display: none;
    }
`;

const StyledDatePickerWrapped = styled(MotiliUIDatePicker)``;

const PortalContent = forwardRef<
    HTMLDivElement,
    { children: ReactNode; style: CSSProperties }
>(({ children, style }, ref) => {
    return createPortal(
        <div ref={ref} style={style}>
            {children}
        </div>,
        document.body
    );
});

export default DatePicker;
